import { useQuery } from "@tanstack/react-query";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { fmtNum, fmtPct } from "@/lib/format";

const COLORS = ["#2563eb", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444"];

export function ShiftTrend({ date, plantId }) {
  const q = useQuery({ queryKey: ["daily-plan-shift-trend", date, plantId], queryFn: async () => (await api.get("/daily-plan/shift-trend", { params: { reporting_date: date, plant_id: plantId || undefined, days: 7 } })).data });
  const d = q.data;
  if (!d || d.days.length < 2 || d.shifts.length < 2) return null;
  const data = d.days.map((x) => ({ ...x, label: x.date.slice(5) }));
  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="shift-trend">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="pp-label">7-day achievement by shift · {d.from.slice(5)} → {d.to.slice(5)}</div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {d.totals.map((t, i) => <span key={t.shift} className={`rounded px-1.5 py-0.5 font-mono ${t.status === "red" ? "bg-rose-100 text-rose-800" : t.status === "amber" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`shift-trend-total-${t.shift}`}>
            <span className="inline-block h-2 w-2 rounded-sm mr-1 align-middle" style={{ background: COLORS[i % COLORS.length] }} />Shift {t.shift} {t.achieve_pct != null ? fmtPct(t.achieve_pct) : "—"} · {t.days_below_95}/{t.days} days &lt;95%</span>)}
          {d.worst_shift && <span className="flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700" data-testid="shift-trend-worst"><TrendingDown size={11} /> Shift {d.worst_shift} lags over the week</span>}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="qty" tick={{ fontSize: 10 }} tickFormatter={(v) => fmtNum(v)} />
            <YAxis yAxisId="pct" orientation="right" domain={[0, (m) => Math.max(120, Math.ceil(m / 10) * 10)]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v, n) => [n.endsWith("%") ? `${v}%` : fmtNum(v), n]} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <ReferenceLine yAxisId="pct" y={95} stroke="#ef4444" strokeDasharray="4 4" />
            {d.shifts.map((s, i) => <Bar key={`a${s}`} yAxisId="qty" dataKey={`${s}_actual`} name={`Shift ${s} actual`} fill={COLORS[i % COLORS.length]} fillOpacity={0.75} radius={[2, 2, 0, 0]} />)}
            {d.shifts.map((s, i) => <Line key={`p${s}`} yAxisId="pct" type="monotone" dataKey={`${s}_pct`} name={`Shift ${s} %`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
